/** Date-only helpers. Leave dates are YYYY-MM-DD and are compared as calendar days. */

export function pad2(value: number): string {
  return String(value).padStart(2, '0')
}

export function isoDate(year: number, month: number, day: number): string {
  return `${year}-${pad2(month)}-${pad2(day)}`
}

export function parseIsoDate(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day))
}

export function formatIsoDate(date: Date): string {
  return isoDate(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate())
}

export function addDays(iso: string, days: number): string {
  const date = parseIsoDate(iso)
  date.setUTCDate(date.getUTCDate() + days)
  return formatIsoDate(date)
}

export function weekday(iso: string): number {
  return parseIsoDate(iso).getUTCDay()
}

export function isWeekend(iso: string): boolean {
  const day = weekday(iso)
  return day === 0 || day === 6
}

export function inclusiveDays(start: string, end: string): number {
  const ms = parseIsoDate(end).getTime() - parseIsoDate(start).getTime()
  return Math.round(ms / 86_400_000) + 1
}

export function daysInYear(year: number): number {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 366 : 365
}

export function yearOf(iso: string): number {
  return Number(iso.slice(0, 4))
}

export function monthOf(iso: string): number {
  return Number(iso.slice(5, 7))
}

export function eachDate(start: string, end: string): string[] {
  if (end < start) return []
  const dates: string[] = []
  let cursor = start
  while (cursor <= end) {
    dates.push(cursor)
    cursor = addDays(cursor, 1)
  }
  return dates
}

export function monthBounds(month: string): { start: string; end: string } {
  const [yearText, monthText] = month.split('-')
  const year = Number(yearText)
  const monthNumber = Number(monthText)
  const last = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate()
  return {
    start: isoDate(year, monthNumber, 1),
    end: isoDate(year, monthNumber, last),
  }
}

export function formatDisplayDate(iso: string): string {
  return parseIsoDate(iso).toLocaleDateString('en-IE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

export function localToday(now = new Date()): string {
  return isoDate(now.getFullYear(), now.getMonth() + 1, now.getDate())
}
